import { useLayoutEffect, useState } from 'react'

/** UI 辞書 components/select.md「固定幅は最長の選択肢 + 91px 以上」 (左右余白 89 + 枠 2)。 */
const SELECT_CHROME_PX = 91

/**
 * 閉じた状態の値の太さと大きさ (select.md「値 14px / 400」。 14px は `selectFontFix` が当てる)。
 * 書体は包みの computed style から取る (値の `textMd` は app の textStyle 補正で本文と同じ stack)。
 */
const VALUE_FONT_WEIGHT_AND_SIZE = '400 14px'

const isStretchAlignment = (self: string, fromParent: string): boolean => {
  const value = self === 'auto' ? fromParent : self
  return value === 'normal' || value === 'stretch' || value === 'legacy'
}

/**
 * 包み (block の `div`) が器の幅に引き伸ばされる配置か。 引き伸ばされた select は選び替えても
 * 幅が変わらず、 確保すると器が狭くなった時に縮まずはみ出すので確保しない。
 *
 * 内容の幅になるのは、 flex の横並び (伸ばす指定なし) と、 flex の縦並び・grid で stretch しない
 * 配置 (DS `FieldRoot` の既定 `align-items: flex-start` 等) だけ。 block の流れ・表のセル等は
 * 器の幅に従うとみなす。
 */
const isStretchedByContainer = (element: HTMLElement): boolean => {
  const parent = element.parentElement
  if (parent === null) return true
  const parentStyle = getComputedStyle(parent)
  const style = getComputedStyle(element)
  if (parentStyle.display.endsWith('flex')) {
    return parentStyle.flexDirection.startsWith('row')
      ? Number.parseFloat(style.flexGrow) > 0
      : isStretchAlignment(style.alignSelf, parentStyle.alignItems)
  }
  if (parentStyle.display.endsWith('grid')) {
    return isStretchAlignment(style.justifySelf, parentStyle.justifyItems)
  }
  return true
}

/**
 * DS Select (react-select) は選択中の値の幅に縮むので、 選び替えるたびに幅が変わり、 右隣の
 * 絞り込みが横にずれる。 一番長い選択肢が収まる幅 (canvas で測った字幅 + 91px) を `minWidth` として
 * 確保する。 器に引き伸ばされている select には確保しない (`isStretchedByContainer`)。
 *
 * 使い方: `ref` を DS Select を包む要素に渡し、 `minWidth` をその要素に当てる。
 * `DsSelect` は幅の指定 (`width` / `minWidth` / `maxWidth`) が無い時、 内側でこれを使う。
 */
export function useWidestOptionMinWidth(labels: readonly string[]): {
  ref: (el: HTMLDivElement | null) => void
  minWidth: string | undefined
} {
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const [minWidth, setMinWidth] = useState<string>()
  const labelsKey = labels.join('\n')

  useLayoutEffect(() => {
    const context =
      element === null || labelsKey === '' || isStretchedByContainer(element)
        ? null
        : document.createElement('canvas').getContext('2d')
    if (element === null || context === null) {
      setMinWidth(undefined)
      return
    }
    context.font = `${VALUE_FONT_WEIGHT_AND_SIZE} ${getComputedStyle(element).fontFamily}`
    const widest = Math.max(
      ...labelsKey.split('\n').map((label) => context.measureText(label).width),
    )
    setMinWidth(`${Math.ceil(widest) + SELECT_CHROME_PX}px`)
  }, [element, labelsKey])

  return { ref: setElement, minWidth }
}
